/**
 * Manages all errors/successes that are thrown throughout the application.  Automatically catches many server thrown errors.
 * If there are specific options provided, it will also throw a success event.
 *
 * This manager publishes several messages all under the error namespace
 *
 * error/auth
 *       A request indicated that the user is not logged in.  Will be passed true if the user's license was revoked
 *
 * error/malformed
 *        A non-200 HTTP status code was received from the server, but the response could not be parsed
 *        Will be passsed an object containing the status code and response text from the server
 *
 * error/message
 *        A well-formed error was received from the server, with an associated localeErrorCode
 *        Will be passed an Error object
 *
 * error/message/XXXXX
 *        A well-formed error was received from the server with the localeErrorCode XXXXX.
 *        Will be passed an Error object.
 *        This message will be thrown before error/messsage.  If something handles this message
 *        locally, the error/message will not be thrown
 *
 * error/server
 *        A well-formed error was received from the server wth no associated localeErrorCode
 *        Will be passed an Error object
 *
 * error/timeout
 *        No response was received from the server
 *        Will be passed an empty object
 *
 * error/client
 *        An error generated from client code.
 *        Will be passed an Error object with the message field set
 *
 * success/message
 *        The request was successfully.
 *        In addition, the request was configured with a localized key that can be used to display this success.
 *        This options on the request should be called "successKey".
 *        Will be passed a locale key.
 *
 *
 *    The manager exposes an interface to prevent these messages from being published globally, but
 *    it should probably not be used directly.  The datasource class supports an option localErrors that handles this.
 *
 *    For direct AJAX calls, there is a custom option localErrors available as well.  This option takes a kendo observable object.
 *    All messages will be triggered on that object instead.
 *
 *
 *    If the localErrors option is not used, there is custom method "handle" attached to promise object returned by $.ajax.
 *
 *    Handle is like "one" on an ObservableObject.  It can bind to any of the error messages generated by that request.  If it doesn't handle a message
 *    the global message will be published.
 */
import loggingOperations from "common/datasources/logging/loggingOperations";
import topic from "common/managers/topic";
import Error from "common/models/Error";
import DisplayError from "common/error/DisplayError";

//Generic handlers for certain status codes and error codes
var handlers = {

    /**
     * Forbidden.  The user is not logged in.
     */
    "403": function() {
        errorManager.authError();
    },

    "401": function() {
        //This should probably not ever occur.  However, a bug in session servlet currently causes it
        //to throw a 401 response on occasion.  This will ensure the user is redirected to the login page.
        errorManager.authError();
    },

    /**
     * Not found error.  This should probably only happen if a path is given incorrectly in code
     */
    "404": function() {
        // TODO: Log 404 - setting.url?
    },

    /**
     * User license has been revoked.  User needs to logout
     */
    "LIC003": function() {
        errorManager.authError();
    }
};

//Catch jQuery ajax errors that aren't dealt with
$(document).ajaxError(function(e, jqxhr, settings, exception) {
    if (exception == "timeout" || jqxhr.status == 0) {
        // TODO: Log request timed out locally
        errorManager.publish(jqxhr, "error/timeout", {});
        return;
    }


    var response = {};
    try {
        response = $.parseJSON(jqxhr.responseText);
    } catch (e) {
        //response didn't return valid json
        if (handlers[jqxhr.status]) {
            return handlers[jqxhr.status]();
        } else {
            // TODO: Log no defined way to handle this error.  This is the worst case scenario.
            errorManager.publish(jqxhr, "error/malformed", {
                status: jqxhr.status,
                response: jqxhr.responseText
            });
            return;
        }
    }

    response.statusCode = jqxhr.status;

    var err = new Error(response);

    var errorCode = err.get("localeErrorCode");
    //The DocFinity server threw some error
    if (errorCode) {
        //the error should have a message attached, and the user most likely caused this
        if (handlers[errorCode]) {
            //This error code is one that the error manager knows how to deal with
            handlers[errorCode]();
        } else {
            // TODO: Log this is a specific, user-presentable error that the error manager does not know how to handle
            errorManager.publish(jqxhr, "error/message/" + errorCode, err) || errorManager.publish(jqxhr, "error/message", err);
        }
    } else {
        // TODO: Log there is no attached error.  The client code most likely caused this.
        //In any case, it is unlikely that the UI can recover from this
        //the only piece of data that is likely to be known is the type of exception the server
        errorManager.publish(jqxhr, "error/server", err);
    }
});

$(document).ajaxSuccess(function(e, jqxhr, options) {
    if (options.successKey) {
        errorManager.publish(jqxhr, "success/message", {
            successKey: options.successKey
        });
    }
});

var errorManager = {

    /**
     * Trigger global error handling for some client generated error.
     * @param message A message that can be displayed to the user
     * @param cause {string} The cause of the error
     * @param stack {string} The error stack trace
     */
    error: function(message, cause, stack) {
        var err = new Error({
            message: message,
            cause: cause,
            stackTrace: stack
        });
        topic.publish("error/client", err);
    },

    /**
     * Trigger the auth error event
     */
    authError: function() {
        topic.publish("error/auth");
    },

    /**
     * Unwrap a partial success and turn it into a standard error.
     * This is useful in cases where a webservice is using partial success, but the UI wants generic error handling.
     * This only really makes to use when only a single item is being passed to the webservice.  However, it does support multiple failures.
     *
     * @param request The promise of the request
     * @param errorCodes Array of error codes that indicate partial success for this call
     */
    unwrapPartialSuccess: function(request, errorCodes) {
        errorCodes.forEach(function(errorCode) {
            request.handle("error/message/" + errorCode, function(data) {
                data.successFailureReportDto.failureDetails.forEach(function(failure) {
                    var err = new Error({
                        localeErrorCode: failure.failureCode,
                        message: failure.failureCause
                    });
                    errorManager.publish(request, "error/message/" + failure.failureCode, err) || errorManager.publish(request, "error/message", err);
                });
            });
        })
    },

    /**
     * Trigger global success handling for some client generated success.
     * @param message Locale key
     */
    success: function(message) {
        topic.publish("success/message", {
            successKey: message
        });
    },

    /**
     * Cause the error manager to trigger an event any time a message would be published globally for a given request.
     * This does not suppress the global message.
     * @param xhr The request object
     * @param observable The observable to redirect errors to.  This may be null(a new object will be created)
     * @returns {*} The observable that errors will be triggered on
     */
    receiveErrors: function(xhr, observable) {
        if (xhr.xhr) {
            xhr = xhr.xhr;
        }
        if (observable == undefined) {
            observable = new kendo.Observable();
        }
        $(xhr).data("errorManagerTriggerOn", observable);

        return observable;
    },

    /**
     * Publish a message.
     * @param jqxhr Request object
     * @param name Name of event
     * @param data Type to pass
     * @returns {boolean}
     */
    publish: function(jqxhr, name, data) {
        var suppress = $(jqxhr).data("errorManagerSuppress");
        var triggerOn = $(jqxhr).data("errorManagerTriggerOn");
        var prevent = false;
        if (triggerOn && triggerOn.trigger) {
            prevent = triggerOn.trigger(name, data);
        }
        if (suppress !== true && prevent !== true) {
            topic.publish(name, data);
        }
        return prevent;
    },

    /**
     * Prevent the error manager from publishing global message for a given request.
     * @param xhr The request object
     */
    suppressMessages: function(xhr) {
        if (xhr.xhr) {
            xhr = xhr.xhr;
        }
        $(xhr).data("errorManagerSuppress", true);
    }

};

//Add custom option for local errors
$.ajaxPrefilter(function(options, originalOptions, jqXHR) {
    if (options.localErrors) {
        errorManager.suppressMessages(jqXHR);
        errorManager.receiveErrors(jqXHR, options.localErrors);
    } else {
        //This extends the promise interface
        var ob = errorManager.receiveErrors(jqXHR);
        jqXHR.handle = function(type, callback) {
            ob.one(type, function(e) {
                e.preventDefault();
                callback(e);
            });
            return jqXHR;
        };
    }
});

window.onerror = function(message, source, lineno, colno, error) {
    let display = "";
    let stack = [];
    if(error) {
        if(error instanceof DisplayError) {
            display = error.message;
        }
        if(error.stack && error.stack.split) {
            stack = error.stack.split("\n");
        }
    }
    if(document.body.parentElement.classList.contains("loading")) {
        //A JS Error has happened during initial boot of the application
        //This is the worst case scenario.  Its possible that literally nothing is available to help report
        //Localization, CSS, and even $.ajax could all be broken
        //Report the error directly to the screen and do it without relying on anything else
        stack.unshift(message);
        const toDisplay = stack.join("\n");
        document.body.innerHTML = `
        <div style="height: 100%; width: 100%; overflow: auto">
            <h1 style="color: #336699; font-size: 1.5em; padding: 10px;">Oops! Something went wrong.</h1>
            <p style="padding: 0px 10px 10px; font-size: 1em;">Please contact your Administrator.</p>
            <pre style="font-size: .85em; padding: 10px; border-bottom: 1px solid #336699; border-top: 1px solid #336699">${toDisplay}</pre>
        </div>
        `;
        document.body.parentElement.classList.remove("loading");
    }
    loggingOperations.logError(message, stack, true);
    errorManager.error(display, message, stack);
};


export default errorManager;
